/**
 * useMenuTypes Hook
 * React hook for managing menu types data
 */

import { useState, useEffect, useCallback } from 'react';
import { menuTypesService, MenuType, CreateMenuTypeDto, UpdateMenuTypeDto } from '../services/menuTypes.service';

interface UseMenuTypesState {
  menuTypes: MenuType[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

export function useMenuTypes(tenantId?: string) {
  const [state, setState] = useState<UseMenuTypesState>({
    menuTypes: [],
    loading: false,
    submitting: false,
    error: null,
  });

  const fetchMenuTypes = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await menuTypesService.getMenuTypes(tenantId);
      
      let menuTypesData: MenuType[] = [];
      if (response.data) {
        menuTypesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
      }

      setState({ menuTypes: menuTypesData, loading: false, submitting: false, error: null });
    } catch (err: any) {
      console.error('Failed to fetch menu types:', err);
      setState({
        menuTypes: [],
        loading: false,
        submitting: false,
        error: err.message || 'Failed to load menu types',
      });
    }
  }, [tenantId]);

  useEffect(() => {
    fetchMenuTypes();
  }, [fetchMenuTypes]);

  const createMenuType = useCallback(async (data: CreateMenuTypeDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await menuTypesService.createMenuType(data);
      await fetchMenuTypes();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchMenuTypes]);

  const updateMenuType = useCallback(async (id: string, data: UpdateMenuTypeDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await menuTypesService.updateMenuType(id, data);
      await fetchMenuTypes();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchMenuTypes]);

  const deleteMenuType = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await menuTypesService.deleteMenuType(id);
      await fetchMenuTypes();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchMenuTypes]);

  const refresh = useCallback(() => {
    fetchMenuTypes();
  }, [fetchMenuTypes]);

  return {
    menuTypes: state.menuTypes,
    loading: state.loading,
    submitting: state.submitting,
    error: state.error,
    createMenuType,
    updateMenuType,
    deleteMenuType,
    refresh,
  };
}
